import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "use-intl";
import { AlbumGrid } from "@/components/album-grid";
import { ActiveNotificationIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, SearchUnavailableIcon } from "@/components/appIcons";
import { FacetFilterControls, MobileFacetFiltersDisclosure } from "@/components/facets/facet-filter-controls";
import { FormatFilterControl } from "@/components/format-filter-control";
import { Link } from "@/components/link";
import { OfferSortSelector } from "@/components/marketplace/offer-sort-selector";
import { OfferSummaryCta } from "@/components/marketplace/offer-summary-cta";
import { OfferPriceFilter } from "@/components/marketplace/OfferPriceFilter";
import { OfferSummaryMetadata } from "@/components/marketplace/OfferSummaryMetadata";
import { UsedOffersFilter } from "@/components/marketplace/UsedOffersFilter";
import { AlertsCallout } from "@/components/messaging/alerts-callout";
import { TrackingScope } from "@/components/TrackingScope";
import { FilterToolbarControl, FilterToolbarControls } from "@/components/ui/filter-toolbar";
import { OFFERS_SURFACE } from "@/facets/config";
import { useFacetFilters } from "@/facets/use-facet-filters";
import { useCurrency } from "@/hooks/use-currency";
import { useReleaseGroupFilterOptions } from "@/hooks/use-release-group-filter-options";
import { useTopLoaderOnRefetch } from "@/hooks/use-top-loader-on-refetch";
import { buildFacetedSearchState, OFFERS_URL_CONFIG, writeFacetedSearchState } from "@/lib/faceted-search-url";
import { buildOffersSeoDescription, buildOffersSeoHeading } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { USER_SELECTABLE_CURRENCIES } from "@garimpa/lib/currencies";
import { DISCOVERY_DECADES } from "@garimpa/lib/discoveryDecades";
import { FORMAT_CATEGORIES_WITH_OTHER } from "@garimpa/lib/formatGroups";
import { normalizeStringArray } from "@garimpa/lib/normalizeStringArray";
import { hasOffersPageFilters } from "./offers-page-state";
import { getFormat, getOffersQueryOptions, getSort, OFFERS_PAGE_SIZE } from "./offers-query";
const OFFER_FORMATS = ["all", ...FORMAT_CATEGORIES_WITH_OTHER] as const;
const paginationButtonClassName = "inline-flex min-w-0 items-center justify-center gap-1 rounded-md bg-secondary px-3 py-2 text-sm font-semibold leading-none transition-colors hover:bg-secondary/80 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-4";
type OffersSearch = {
  condition?: "new" | "used";
  artistId?: string[];
  cursor?: number;
  format?: (typeof OFFER_FORMATS)[number];
  genre?: string[];
  /** Hidden minimum work popularity score for data inspection. */
  popularity?: number;
  priceMin?: number;
  priceMax?: number;
  sort?: (typeof OFFER_SORTS)[number];
  style?: string[];
  year?: number[];
};
const OFFER_DECADE_OPTIONS = DISCOVERY_DECADES.map(decade => ({
  value: `${decade.startYear}-${decade.endYear}`,
  label: decade.label
}));

/** Keeps the facet `year` shape: one exact year or a closed [from, to] range. */
import { Route } from "/app/apps/web/src/routes/_app/offers/index.tsx";
type OfferFormat = (typeof OFFER_FORMATS)[number];
function useLastDefinedValue<T>(value: T | undefined) {
  const lastValue = useRef(value);
  if (value !== undefined) lastValue.current = value;
  return value ?? lastValue.current;
}
function rememberSuccessfulFilterIdentity(identity: React.RefObject<string>, currentIdentity: string, data: unknown, isPlaceholderData: boolean) {
  if (data && !isPlaceholderData) identity.current = currentIdentity;
}
function arrayOrEmpty<T>(value: T[] | undefined): T[] {
  return value ?? [];
}
function useOffersRouteData(search: OffersSearch, currency: string) {
  const filterIdentity = JSON.stringify({
    condition: search.condition ?? "new",
    artistId: search.artistId,
    currency,
    cursor: search.cursor,
    format: getFormat(search.format),
    genre: search.genre,
    popularity: search.popularity,
    priceMin: search.priceMin,
    priceMax: search.priceMax,
    style: search.style,
    year: search.year
  });
  const lastSuccessfulFilterIdentity = useRef(filterIdentity);
  const preserveCardsWhileSorting = lastSuccessfulFilterIdentity.current === filterIdentity;
  const query = useQuery({
    ...getOffersQueryOptions({
      ...search,
      currency
    }),
    placeholderData: preserveCardsWhileSorting ? keepPreviousData : () => undefined
  });
  rememberSuccessfulFilterIdentity(lastSuccessfulFilterIdentity, filterIdentity, query.data, query.isPlaceholderData);
  const offers = arrayOrEmpty(query.data?.offers);
  const formatCounts = useLastDefinedValue(query.data?.formatCounts);
  const genreCounts = useLastDefinedValue(query.data?.genreCounts);
  const styleCounts = useLastDefinedValue(query.data?.styleCounts);
  return {
    formatCounts,
    genreCounts: arrayOrEmpty(genreCounts),
    offers,
    query,
    styleCounts: arrayOrEmpty(styleCounts)
  };
}
function activeAdvancedFilterCount(search: OffersSearch) {
  return Number(search.condition === "used") + normalizeStringArray(search.genre).length + normalizeStringArray(search.style).length + Number(search.priceMin !== undefined) + Number(search.priceMax !== undefined) + Number(Boolean(search.year?.length));
}
function offerFormatCount(counts: Partial<Record<OfferFormat, number>> | undefined, format: OfferFormat) {
  return counts?.[format] ?? 0;
}
function OffersRoute() {
  const t = useTranslations("shared.affiliates");
  const tAlerts = useTranslations("alerts");
  const tHome = useTranslations("home");
  const tc = useTranslations("shared.collection");
  const tg = useTranslations("shared.genres");
  const ts = useTranslations("shared.styles");
  const trf = useTranslations("shared.releaseFilters");
  const locale = useLocale();
  const search = Route.useSearch();
  const {
    artist
  } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const facetState = buildFacetedSearchState(search, OFFERS_URL_CONFIG);
  const onFacetStateChange = useCallback((next: FacetedSearchState | undefined) => {
    void navigate({
      search: prev => ({
        ...writeFacetedSearchState(prev, next, OFFERS_URL_CONFIG),
        cursor: undefined
      }),
      replace: false,
      resetScroll: false
    });
  }, [navigate]);
  const facetControls = useFacetFilters({
    state: facetState,
    onChange: onFacetStateChange,
    surface: OFFERS_SURFACE
  });
  const {
    currency,
    setCurrency,
    isPending: isCurrencyPending
  } = useCurrency();
  const {
    formatCounts,
    genreCounts,
    offers,
    query: offersQuery,
    styleCounts
  } = useOffersRouteData(search, currency);
  const taxonomyOptions = useReleaseGroupFilterOptions({
    genreCounts,
    styleCounts
  }, locale);
  const facetOptions = {
    ...taxonomyOptions,
    year: OFFER_DECADE_OPTIONS
  };
  useTopLoaderOnRefetch(offersQuery.isFetching);
  const offerAlbums = offers.map(offer => ({
    ...offer.album,
    addToListsTarget: {
      entityType: "release" as const,
      releaseId: offer.album.releaseId,
      workId: offer.workId
    },
    offerSummary: offer,
    offer
  }));
  const hasFormatCounts = formatCounts !== undefined;
  const cursor = search.cursor ?? 0;
  const selectedFormat = getFormat(search.format);
  const selectedSort = getSort(search.sort);
  const selectedFormatCount = offerFormatCount(formatCounts, selectedFormat);
  const advancedFilterCount = activeAdvancedFilterCount(search);
  const isOffersLoading = offersQuery.isLoading && offers.length === 0;
  const skeletonCount = Math.min(Math.max(selectedFormatCount, 1), OFFERS_PAGE_SIZE);
  const trackedOffersView = useRef(false);
  useEffect(() => {
    if (trackedOffersView.current) return;
    trackedOffersView.current = true;
  }, []);
  const offerSortMode: OfferSortMode = selectedSort === "best_price" ? "best_price" : "deals";
  const translators = {
    offers: t,
    collection: tc,
    genres: tg,
    styles: ts
  };
  const seoInput = {
    ...search,
    artistName: artist?.name,
    currency
  };
  const pageTitle = buildOffersSeoHeading(seoInput, translators, locale);
  const pageDescription = buildOffersSeoDescription(seoInput, translators, locale);
  const updateSearch = (patch: Partial<OffersSearch>, options: {
    resetScroll?: boolean;
  } = {}) => {
    void navigate({
      search: prev => ({
        ...prev,
        ...patch,
        cursor: patch.cursor
      }),
      replace: false,
      resetScroll: options.resetScroll ?? false
    });
  };
  const updatePageCursor = (nextCursor: number | undefined) => {
    updateSearch({
      cursor: nextCursor
    }, {
      resetScroll: true
    });
  };
  const filterSearch = (patch: Partial<OffersSearch>): OffersSearch => ({
    ...search,
    ...patch,
    cursor: undefined
  });
  const handleEmptyStateCurrencyChange = (nextCurrency: string) => {
    if (nextCurrency === currency) return;
    setCurrency(nextCurrency);
    updateSearch({
      cursor: undefined
    }, {
      resetScroll: false
    });
  };
  const renderResults = () => {
    if (!isOffersLoading && offers.length === 0) {
      return <OffersEmptyState currency={currency} disabled={isCurrencyPending} showClearFilters={hasOffersPageFilters(search)} onCurrencyChange={handleEmptyStateCurrencyChange} />;
    }
    return <>
        <TrackingScope surface="offers_index" entityType="release">
          <AlbumGrid albums={offerAlbums} isLoading={isOffersLoading} skeletonCount={skeletonCount} itemKey={album => album.offer.offerId} priorityCount={2} link={album => ({
          to: "/release/$id",
          params: {
            id: album.releaseId
          }
        })} linkCoversFooter showAlbumType={false} showOfferSummary={false} tags={album => <OfferSummaryMetadata offer={album.offer} />} footer={album => <OfferSummaryCta offer={album.offer} summary={album.offer.offerSummaryByCondition} />} />
        </TrackingScope>
        {(isOffersLoading || offers.length > 0) && <OffersPagination className="mt-8" cursor={cursor} nextCursor={offersQuery.data?.nextCursor ?? undefined} previousLabel={t("previousPage")} nextLabel={t("nextPage")} onCursorChange={updatePageCursor} />}
      </>;
  };
  return <main className="mx-auto max-w-[1440px] px-4 py-8 md:px-10 lg:px-20">
      <div className="mb-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">{pageTitle}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{pageDescription}</p>
          </div>
          <Link to="/alerts" className="inline-flex min-h-11 shrink-0 items-center gap-1.5 self-start text-sm font-semibold text-muted-foreground transition-colors hover:text-primary">
            <ActiveNotificationIcon className="text-lg" decorative />
            {tAlerts("navCta")}
          </Link>
        </div>

        <div className="space-y-3">
          <div className="flex min-w-0 items-center gap-3">
            <FormatFilterControl wrapperClassName="min-w-0 flex-1" aria-label={tHome("formatFilters")} options={FORMAT_CATEGORIES_WITH_OTHER.filter(format => !hasFormatCounts || formatCounts?.[format] || format === selectedFormat).map(format => ({
            value: format,
            count: formatCounts?.[format]
          }))} value={selectedFormat} all={{
            count: formatCounts?.all
          }} linkTarget={format => ({
            to: "/offers",
            search: filterSearch({
              format: format === "all" ? undefined : format as OfferFormat
            })
          })} />
            <div className="ml-auto hidden sm:flex">
              <UsedOffersFilter checked={search.condition === "used"} onChange={checked => updateSearch({
              condition: checked ? "used" : undefined,
              cursor: undefined
            })} />
            </div>
          </div>

          <div className="space-y-3">
            <FilterToolbarControls label={trf("filterBy")} desktopGapClassName="sm:gap-2" className="min-w-0">
              <MobileFacetFiltersDisclosure activeCount={advancedFilterCount} controlsId="offers-advanced-filters" open={advancedFiltersOpen} onToggle={() => setAdvancedFiltersOpen(open => !open)} />

              <div id="offers-advanced-filters" className={cn("col-span-2 grid grid-cols-2 gap-2", !advancedFiltersOpen && "hidden", "sm:grid sm:w-full sm:grid-cols-4 sm:[&>div]:col-span-1 sm:[&>div]:min-w-0")}>
                <FilterToolbarControl spanClassName="col-span-2" className="sm:hidden">
                  <UsedOffersFilter checked={search.condition === "used"} onChange={checked => updateSearch({
                  condition: checked ? "used" : undefined,
                  cursor: undefined
                })} />
                </FilterToolbarControl>
                <FacetFilterControls surface={OFFERS_SURFACE} options={facetOptions} controller={facetControls} showClear={false} />

                <FilterToolbarControl className="sm:min-w-0">
                  <OfferPriceFilter priceMin={search.priceMin} priceMax={search.priceMax} currency={currency} onChange={range => updateSearch({
                  priceMin: range.priceMin,
                  priceMax: range.priceMax,
                  cursor: undefined
                })} />
                </FilterToolbarControl>
              </div>
            </FilterToolbarControls>
            <div className="flex justify-end">
              <OfferSortSelector value={offerSortMode} getLinkTarget={value => ({
              to: "/offers",
              search: filterSearch({
                // A missing sort already means price_drop.
                sort: value === "best_price" ? "best_price" : undefined
              })
            })} />
            </div>
          </div>
        </div>
      </div>

      {renderResults()}
      <AlertsCallout surface="offers_index" />
    </main>;
}
function OffersEmptyState({
  currency,
  disabled,
  showClearFilters,
  onCurrencyChange
}: {
  currency: string;
  disabled: boolean;
  showClearFilters: boolean;
  onCurrencyChange: (currency: string) => void;
}) {
  const t = useTranslations("shared.affiliates");
  const tm = useTranslations("shared.marketplace");
  const trf = useTranslations("shared.releaseFilters");
  return <section className="mx-auto flex max-w-xl flex-col items-center rounded-md border border-border bg-secondary/30 px-4 py-10 text-center sm:px-6">
      <SearchUnavailableIcon className="text-3xl text-muted-foreground" aria-hidden="true" />
      <h2 className="mt-3 text-base font-semibold text-foreground">{t("noOffersFound")}</h2>
      {showClearFilters && <Link to="/offers" search={{}} className="mt-4 inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
          {trf("clearFilters")}
        </Link>}
      <div className="mt-6 w-full">
        <p className="text-xs font-semibold uppercase text-muted-foreground">
          {tm("currencyPreference")}
        </p>
        <div role="group" aria-label={tm("selectCurrency")} className="mt-2 grid grid-cols-3 gap-2">
          {USER_SELECTABLE_CURRENCIES.map(option => {
          const active = option.code === currency;
          return <button key={option.code} type="button" aria-pressed={active} disabled={disabled} onClick={() => onCurrencyChange(option.code)} className={cn("flex min-h-12 items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold transition-colors disabled:cursor-wait disabled:opacity-60", active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:bg-secondary")}>
                <span className="opacity-75">{option.symbol}</span>
                <span>{option.code}</span>
                {active && <CheckIcon className="text-[16px]" aria-hidden="true" />}
              </button>;
        })}
        </div>
      </div>
    </section>;
}
function OffersPagination({
  className,
  cursor,
  nextCursor,
  previousLabel,
  nextLabel,
  onCursorChange
}: {
  className?: string;
  cursor: number;
  nextCursor: number | undefined;
  previousLabel: string;
  nextLabel: string;
  onCursorChange: (cursor: number | undefined) => void;
}) {
  return <div className={cn("grid w-full grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-end sm:gap-3", className)}>
      <button type="button" disabled={cursor <= 0} onClick={() => onCursorChange(cursor > OFFERS_PAGE_SIZE ? cursor - OFFERS_PAGE_SIZE : undefined)} className={cn(paginationButtonClassName, "w-full")}>
        <ChevronLeftIcon className="shrink-0 text-[18px]" aria-hidden="true" />
        <span className="truncate">{previousLabel}</span>
      </button>
      <button type="button" disabled={!nextCursor} onClick={() => onCursorChange(nextCursor)} className={cn(paginationButtonClassName, "w-full")}>
        <span className="truncate">{nextLabel}</span>
        <ChevronRightIcon className="shrink-0 text-[18px]" aria-hidden="true" />
      </button>
    </div>;
}
export { OffersRoute as component };